import { useState } from "react";
import { Download, Loader2, MailCheck, MailWarning } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const GUIDE_META = {
  airport: { title: "The Free Nigerian Airport Guide" },
  school: { title: "Best Nigerian Private School Guide" },
};

export const GuideRequestButton = ({ kind, label = "Get the Free Guide", testid, className }) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // {download_url, email_sent, message}
  const meta = GUIDE_META[kind];

  const reset = () => {
    setResult(null);
    setConsent(false);
    setMarketing(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.post(`/guides/${kind}/request`, {
        email,
        consent,
        marketing_opt_in: marketing,
      });
      setResult(res.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (v) reset();
      }}
    >
      <DialogTrigger asChild>
        <button data-testid={testid} className={className}>
          {label}
        </button>
      </DialogTrigger>
      <DialogContent className="border-jawa-stone bg-white text-jawa-espresso sm:max-w-md" data-testid={`${testid}-modal`}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl font-medium text-jawa-espresso">{meta.title}</DialogTitle>
          <DialogDescription className="text-sm text-jawa-espresso/60">
            Free — we email you the download link, and your download starts right here too.
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div data-testid={`${testid}-confirmation`}>
            <p className={`flex items-start gap-2 text-sm leading-relaxed ${result.email_sent ? "text-jawa-espresso" : "text-jawa-espresso/80"}`}>
              {result.email_sent ? (
                <MailCheck className="mt-0.5 h-4 w-4 shrink-0 text-jawa-turquoise" />
              ) : (
                <MailWarning className="mt-0.5 h-4 w-4 shrink-0 text-jawa-gold" />
              )}
              <span data-testid={`${testid}-status`}>{result.message}</span>
            </p>
            <a
              href={result.download_url}
              data-testid={`${testid}-download`}
              className="mt-6 flex w-full items-center justify-center gap-2 bg-jawa-coral px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-jawa-espresso"
            >
              <Download className="h-4 w-4" /> Download now (PDF)
            </a>
            {!result.email_sent && (
              <button
                onClick={() => setResult(null)}
                data-testid={`${testid}-retry`}
                className="mt-3 w-full text-center text-xs font-semibold uppercase tracking-[0.2em] text-jawa-turquoise transition-colors hover:text-jawa-espresso"
              >
                Retry the email
              </button>
            )}
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid={`${testid}-form`}>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email address"
              data-testid={`${testid}-email`}
              className="w-full border border-jawa-espresso/20 bg-white px-4 py-3 text-sm text-jawa-espresso outline-none transition-colors placeholder:text-jawa-espresso/40 focus:border-jawa-turquoise"
            />
            <label className="flex items-start gap-3 text-xs leading-relaxed text-jawa-espresso/70">
              <input
                type="checkbox"
                required
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                data-testid={`${testid}-consent`}
                className="mt-0.5"
              />
              I agree to JÀWÀ AFRICA using my email address to send me the guide I have requested.
            </label>
            <label className="flex items-start gap-3 text-xs leading-relaxed text-jawa-espresso/60">
              <input
                type="checkbox"
                checked={marketing}
                onChange={(e) => setMarketing(e.target.checked)}
                data-testid={`${testid}-marketing`}
                className="mt-0.5"
              />
              Also send me occasional JÀWÀ updates and offers (optional — no spam, unsubscribe any time).
            </label>
            <button
              type="submit"
              disabled={busy}
              data-testid={`${testid}-submit`}
              className="flex w-full items-center justify-center gap-2 bg-jawa-espresso px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-ivory transition-colors hover:bg-jawa-coral disabled:opacity-60"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              Email me the guide
            </button>
            <p className="text-[11px] leading-relaxed text-jawa-espresso/45">
              We use your email only to deliver this guide (and updates if you opt in). Stored securely,
              never shared or published.
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
